import React from 'react';
import { Plus, Trash, MusicNote, BookOpen, Translate, FileText } from '@phosphor-icons/react';

export default function PoemModule({ metadata, onChange }) {
  const handleChange = (field, value) => {
    onChange({ ...metadata, [field]: value });
  };

  const handleStanzaChange = (index, value) => {
    const newStanzas = [...(metadata.stanzas || [])];
    newStanzas[index] = value;
    handleChange('stanzas', newStanzas);
  };

  const addStanza = () => {
    handleChange('stanzas', [...(metadata.stanzas || []), '']);
  };

  const removeStanza = (index) => {
    const newStanzas = (metadata.stanzas || []).filter((_, i) => i !== index);
    handleChange('stanzas', newStanzas);
  };

  return (
    <div className="space-y-6">
      {/* Stanzas */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <label className="text-sm font-medium flex items-center gap-2">
            <FileText size={16} />
            Stanzas <span className="text-red-500">*</span>
          </label>
          <button
            onClick={addStanza}
            className="flex items-center gap-1 px-3 py-1 text-sm rounded-lg bg-primary text-primary-foreground hover:opacity-90"
          >
            <Plus size={14} weight="bold" />
            Add Stanza
          </button>
        </div>

        {(metadata.stanzas || []).length === 0 && (
          <button
            onClick={addStanza}
            className="w-full p-8 border-2 border-dashed border-border rounded-lg hover:border-primary transition-colors"
          >
            <div className="text-center">
              <FileText size={32} className="mx-auto mb-2 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">Click to add your first stanza</p>
            </div>
          </button>
        )}

        <div className="space-y-4">
          {(metadata.stanzas || []).map((stanza, index) => (
            <div key={index} className="relative group">
              <div className="absolute -left-8 top-2 text-xs text-muted-foreground font-mono">
                {index + 1}
              </div>
              <textarea
                value={stanza}
                onChange={(e) => handleStanzaChange(index, e.target.value)}
                placeholder="Write stanza here (line breaks preserved)..."
                className="w-full px-4 py-3 bg-background border border-border rounded-lg outline-none focus:border-primary transition-colors resize-none font-serif"
                rows={4}
                style={{ fontFamily: 'Cormorant Garamond, serif', lineHeight: '1.8' }}
              />
              {(metadata.stanzas || []).length > 1 && (
                <button
                  onClick={() => removeStanza(index)}
                  className="absolute -right-10 top-2 p-2 rounded-lg text-red-500 opacity-0 group-hover:opacity-100 hover:bg-red-50 dark:hover:bg-red-950/30 transition-all"
                >
                  <Trash size={16} />
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Poem Type */}
      <div>
        <label className="block text-sm font-medium mb-2">
          Poem Type
        </label>
        <select
          value={metadata.poem_type || ''}
          onChange={(e) => handleChange('poem_type', e.target.value)}
          className="w-full px-4 py-2 bg-background border border-border rounded-lg outline-none focus:border-primary transition-colors"
        >
          <option value="">Select type...</option>
          <option value="lyrical">Lyrical</option>
          <option value="narrative">Narrative</option>
          <option value="epic">Epic</option>
          <option value="free-verse">Free Verse</option>
          <option value="sonnet">Sonnet</option>
          <option value="haiku">Haiku</option>
          <option value="limerick">Limerick</option>
          <option value="ballad">Ballad</option>
          <option value="ode">Ode</option>
        </select>
      </div>

      {/* Mood & Theme */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-2">
            Mood
          </label>
          <input
            type="text"
            value={metadata.mood || ''}
            onChange={(e) => handleChange('mood', e.target.value)}
            placeholder="e.g., Melancholic, Joyful, Nostalgic"
            className="w-full px-4 py-2 bg-background border border-border rounded-lg outline-none focus:border-primary transition-colors"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-2">
            Theme
          </label>
          <input
            type="text"
            value={metadata.theme || ''}
            onChange={(e) => handleChange('theme', e.target.value)}
            placeholder="e.g., Love, Nature, Loss"
            className="w-full px-4 py-2 bg-background border border-border rounded-lg outline-none focus:border-primary transition-colors"
          />
        </div>
      </div>

      {/* Meaning & Interpretation */}
      <div>
        <label className="block text-sm font-medium mb-2 flex items-center gap-2">
          <BookOpen size={16} />
          Meaning / Interpretation
        </label>
        <textarea
          value={metadata.meaning || ''}
          onChange={(e) => handleChange('meaning', e.target.value)}
          placeholder="Explain the meaning and deeper interpretation of your poem..."
          className="w-full px-4 py-3 bg-background border border-border rounded-lg outline-none focus:border-primary transition-colors resize-none"
          rows={4}
        />
      </div>

      {/* Transliteration */}
      <div>
        <label className="block text-sm font-medium mb-2 flex items-center gap-2">
          <Translate size={16} />
          Transliteration
        </label>
        <textarea
          value={metadata.transliteration || ''}
          onChange={(e) => handleChange('transliteration', e.target.value)}
          placeholder="Roman script transliteration (if original is in Devanagari/Urdu)..."
          className="w-full px-4 py-3 bg-background border border-border rounded-lg outline-none focus:border-primary transition-colors resize-none"
          rows={3}
        />
      </div>

      {/* Translation */}
      <div>
        <label className="block text-sm font-medium mb-2 flex items-center gap-2">
          <Translate size={16} />
          Translation
        </label>
        <textarea
          value={metadata.translation || ''}
          onChange={(e) => handleChange('translation', e.target.value)}
          placeholder="English translation..."
          className="w-full px-4 py-3 bg-background border border-border rounded-lg outline-none focus:border-primary transition-colors resize-none"
          rows={4}
        />
      </div>

      {/* Audio Recitation */}
      <div>
        <label className="block text-sm font-medium mb-2 flex items-center gap-2">
          <MusicNote size={16} />
          Audio Recitation URL
        </label>
        <input
          type="url"
          value={metadata.audio_url || ''}
          onChange={(e) => handleChange('audio_url', e.target.value)}
          placeholder="https://example.com/audio.mp3"
          className="w-full px-4 py-2 bg-background border border-border rounded-lg outline-none focus:border-primary transition-colors"
        />
        <p className="text-xs text-muted-foreground mt-1">
          Upload audio to a hosting service and paste the URL here
        </p>
      </div>

      {/* Meter (Optional) */}
      <div>
        <label className="block text-sm font-medium mb-2">
          Meter (Optional)
        </label>
        <input
          type="text"
          value={metadata.meter || ''}
          onChange={(e) => handleChange('meter', e.target.value)}
          placeholder="e.g., Iambic Pentameter"
          className="w-full px-4 py-2 bg-background border border-border rounded-lg outline-none focus:border-primary transition-colors"
        />
      </div>
    </div>
  );
}
